import React, { useEffect, useState } from 'react';
import * as payrollService from '../../services/payrollService';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';

const initialGrantForm = { payroll_employee_id: '', leave_type: 'PL', days: '', date: '', notes: '' };
const initialUsageForm = { payroll_employee_id: '', leave_type: 'PL', days: '', date: '', notes: '' };

// Leave Management (2026-09-14) - a tab on Employee Master, same shape as
// Loans: PL (21 days/yr, granted as one annual lump sum) and COMP_OFF
// (1 day per holiday/week-off worked, expires 60 days after being
// earned). HR/CEO/System_Admin administered only - no employee
// self-service yet (explicit decision, PayrollProfile has no login to
// self-serve through). `profiles` is passed down from
// PayrollEmployeesPage (already loaded there) rather than re-fetched.
const PayrollLeaveTab = ({ profiles }) => {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [showUsageForm, setShowUsageForm] = useState(false);
  const [grantForm, setGrantForm] = useState(initialGrantForm);
  const [usageForm, setUsageForm] = useState(initialUsageForm);
  const [submitting, setSubmitting] = useState(false);
  const [grantingAnnual, setGrantingAnnual] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const showToast = useUIStore((s) => s.showToast);

  const load = () => {
    setLoading(true);
    payrollService.getLeaveBalances()
      .then((res) => {
        const rows = res.data.balances || [];
        setBalances(rows);
        // Default to everyone selected - HR unchecks whoever isn't
        // PL-eligible yet, rather than having to opt everyone in by hand.
        setSelectedIds(new Set(rows.map((b) => b.payroll_employee_id)));
      })
      .catch((err) => showToast(err.response?.data?.detail || 'Failed to load leave balances', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshLedger = (payrollEmployeeId) => {
    setLedgerLoading(true);
    payrollService.getLeaveLedger({ payroll_employee_id: payrollEmployeeId })
      .then((res) => setLedger(res.data.entries || []))
      .catch((err) => showToast(err.response?.data?.detail || 'Failed to load leave history', 'error'))
      .finally(() => setLedgerLoading(false));
  };

  const toggleExpand = (payrollEmployeeId) => {
    if (expandedId === payrollEmployeeId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(payrollEmployeeId);
    refreshLedger(payrollEmployeeId);
  };

  const toggleSelect = (payrollEmployeeId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(payrollEmployeeId)) next.delete(payrollEmployeeId);
      else next.add(payrollEmployeeId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === balances.length ? new Set() : new Set(balances.map((b) => b.payroll_employee_id))
    );
  };

  const handleGrantAnnualPL = async () => {
    if (selectedIds.size === 0) {
      showToast('Select at least one employee to grant PL to', 'error');
      return;
    }
    if (!window.confirm(`Grant 21 PL days to ${selectedIds.size} selected employee${selectedIds.size === 1 ? '' : 's'} who haven't already received a PL grant this year? Unchecked employees will be skipped.`)) return;
    setGrantingAnnual(true);
    try {
      const res = await payrollService.grantAnnualPL(Array.from(selectedIds));
      const { granted_count, skipped_count } = res.data;
      showToast(`Granted PL to ${granted_count} employee${granted_count === 1 ? '' : 's'} (${skipped_count} already had this year's grant)`, 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to grant annual PL', 'error');
    }
    setGrantingAnnual(false);
  };

  const handleGrantSubmit = async (e) => {
    e.preventDefault();
    if (!grantForm.payroll_employee_id || !grantForm.days) {
      showToast('Employee and days are required', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await payrollService.grantLeave({
        payroll_employee_id: Number(grantForm.payroll_employee_id),
        leave_type: grantForm.leave_type,
        days: Number(grantForm.days),
        date: grantForm.date || undefined,
        notes: grantForm.notes || null,
      });
      showToast('Leave granted', 'success');
      setGrantForm(initialGrantForm);
      setShowGrantForm(false);
      load();
      if (expandedId === Number(grantForm.payroll_employee_id)) refreshLedger(expandedId);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to grant leave', 'error');
    }
    setSubmitting(false);
  };

  const handleUsageSubmit = async (e) => {
    e.preventDefault();
    if (!usageForm.payroll_employee_id || !usageForm.days) {
      showToast('Employee and days are required', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await payrollService.recordLeaveUsage({
        payroll_employee_id: Number(usageForm.payroll_employee_id),
        leave_type: usageForm.leave_type,
        days: Number(usageForm.days),
        date: usageForm.date || undefined,
        notes: usageForm.notes || null,
      });
      showToast('Leave usage recorded', 'success');
      setUsageForm(initialUsageForm);
      setShowUsageForm(false);
      load();
      if (expandedId === Number(usageForm.payroll_employee_id)) refreshLedger(expandedId);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to record leave usage', 'error');
    }
    setSubmitting(false);
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(`Delete this ${entry.leave_type} ${entry.transaction_type} entry for ${entry.employee_name}? Only do this if it was entered by mistake.`)) return;
    try {
      await payrollService.deleteLeaveEntry(entry.id);
      showToast('Entry deleted', 'success');
      load();
      if (expandedId === entry.payroll_employee_id) refreshLedger(entry.payroll_employee_id);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete entry', 'error');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-xs text-gray-400 max-w-md">
          PL: 21 days/year, granted once annually. Comp Off: 1 day per holiday/week-off worked, expires 60 days after being earned.
          Use the checkboxes below to leave out anyone not yet PL-eligible.
        </p>
        <div className="flex gap-2">
          <button
            onClick={handleGrantAnnualPL}
            disabled={grantingAnnual}
            className="px-4 py-2 text-sm rounded border border-white/40 text-white hover:bg-white/10 font-semibold disabled:opacity-60"
          >
            {grantingAnnual ? 'Granting...' : `Grant Annual PL to Selected (${selectedIds.size})`}
          </button>
          <button onClick={() => { setShowUsageForm((v) => !v); setShowGrantForm(false); }} className="px-4 py-2 text-sm rounded bg-white/10 hover:bg-white/20 text-white font-semibold">
            {showUsageForm ? 'Cancel' : 'Record Leave Taken'}
          </button>
          <button onClick={() => { setShowGrantForm((v) => !v); setShowUsageForm(false); }} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">
            {showGrantForm ? 'Cancel' : '+ Add Grant'}
          </button>
        </div>
      </div>

      {showGrantForm && (
        <form onSubmit={handleGrantSubmit} className="glass-card p-6 mb-6 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
            <select required value={grantForm.payroll_employee_id} onChange={(e) => setGrantForm({ ...grantForm, payroll_employee_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
              <option value="">Select employee</option>
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.name} {p.employee_code ? `(${p.employee_code})` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type *</label>
            <select value={grantForm.leave_type} onChange={(e) => setGrantForm({ ...grantForm, leave_type: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
              <option value="PL">Privilege Leave</option>
              <option value="COMP_OFF">Comp Off</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Days *</label>
            <input required type="number" step="0.5" value={grantForm.days} onChange={(e) => setGrantForm({ ...grantForm, days: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            {grantForm.leave_type === 'COMP_OFF' && <p className="text-xs text-gray-400 mt-1">Expires 60 days from the date below.</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input type="date" value={grantForm.date} onChange={(e) => setGrantForm({ ...grantForm, date: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <input value={grantForm.notes} onChange={(e) => setGrantForm({ ...grantForm, notes: e.target.value })} placeholder="e.g. Worked Independence Day" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div className="col-span-2">
            <button type="submit" disabled={submitting} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
              {submitting ? 'Saving...' : 'Save Grant'}
            </button>
          </div>
        </form>
      )}

      {showUsageForm && (
        <form onSubmit={handleUsageSubmit} className="glass-card p-6 mb-6 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
            <select required value={usageForm.payroll_employee_id} onChange={(e) => setUsageForm({ ...usageForm, payroll_employee_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
              <option value="">Select employee</option>
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.name} {p.employee_code ? `(${p.employee_code})` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type *</label>
            <select value={usageForm.leave_type} onChange={(e) => setUsageForm({ ...usageForm, leave_type: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
              <option value="PL">Privilege Leave</option>
              <option value="COMP_OFF">Comp Off</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Days Taken *</label>
            <input required type="number" step="0.5" value={usageForm.days} onChange={(e) => setUsageForm({ ...usageForm, days: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input type="date" value={usageForm.date} onChange={(e) => setUsageForm({ ...usageForm, date: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <input value={usageForm.notes} onChange={(e) => setUsageForm({ ...usageForm, notes: e.target.value })} placeholder="e.g. Family function" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div className="col-span-2">
            <button type="submit" disabled={submitting} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
              {submitting ? 'Saving...' : 'Save Usage'}
            </button>
          </div>
        </form>
      )}

      <div className="glass-card">
        {loading ? (
          <p className="text-gray-500 text-sm p-5">Loading...</p>
        ) : (
          <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-medium">
                    {balances.length > 0 && (
                      <input type="checkbox" checked={selectedIds.size === balances.length} onChange={toggleSelectAll} title="Select all - included in Grant Annual PL to All" />
                    )}
                  </th>
                  <th className="px-4 py-2 font-medium">Code</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Branch</th>
                  <th className="px-4 py-2 font-medium text-right">PL Balance</th>
                  <th className="px-4 py-2 font-medium text-right">Comp Off Balance</th>
                  <th className="px-4 py-2 font-medium">History</th>
                </tr>
              </thead>
              <tbody>
                {balances.map((b) => (
                  <React.Fragment key={b.payroll_employee_id}>
                    <tr className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selectedIds.has(b.payroll_employee_id)} onChange={() => toggleSelect(b.payroll_employee_id)} />
                      </td>
                      <td className="px-4 py-3 text-gray-500">{b.employee_code || '—'}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{b.name}</td>
                      <td className="px-4 py-3">{b.branch || '—'}</td>
                      <td className={`px-4 py-3 text-right font-semibold ${b.pl_balance < 0 ? 'text-red-600' : 'text-gray-900'}`}>{b.pl_balance}</td>
                      <td className={`px-4 py-3 text-right font-semibold ${b.comp_off_balance < 0 ? 'text-red-600' : 'text-gray-900'}`}>{b.comp_off_balance}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleExpand(b.payroll_employee_id)} className="text-komal-blue hover:underline text-xs font-semibold">
                          {expandedId === b.payroll_employee_id ? 'Hide' : 'View'}
                        </button>
                      </td>
                    </tr>
                    {expandedId === b.payroll_employee_id && (
                      <tr className="bg-gray-50">
                        <td colSpan={7} className="px-4 py-3">
                          {ledgerLoading ? (
                            <p className="text-xs text-gray-400">Loading history...</p>
                          ) : ledger.length === 0 ? (
                            <p className="text-xs text-gray-400">No leave transactions yet.</p>
                          ) : (
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-gray-400">
                                  <th className="py-1 pr-4 font-medium">Date</th>
                                  <th className="py-1 pr-4 font-medium">Type</th>
                                  <th className="py-1 pr-4 font-medium">Transaction</th>
                                  <th className="py-1 pr-4 font-medium text-right">Days</th>
                                  <th className="py-1 pr-4 font-medium">Expires</th>
                                  <th className="py-1 pr-4 font-medium">Notes</th>
                                  <th className="py-1 font-medium"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {ledger.map((entry) => (
                                  <tr key={entry.id} className="border-t border-gray-200">
                                    <td className="py-1.5 pr-4 text-gray-500">{entry.date}</td>
                                    <td className="py-1.5 pr-4">{entry.leave_type === 'PL' ? 'Privilege Leave' : 'Comp Off'}</td>
                                    <td className="py-1.5 pr-4 capitalize">{entry.transaction_type}</td>
                                    <td className={`py-1.5 pr-4 text-right font-semibold ${entry.days < 0 ? 'text-red-600' : 'text-green-700'}`}>{entry.days > 0 ? '+' : ''}{entry.days}</td>
                                    <td className="py-1.5 pr-4 text-gray-500">{entry.expiry_date || '—'}</td>
                                    <td className="py-1.5 pr-4 text-gray-500">{entry.notes || '—'}</td>
                                    <td className="py-1.5">
                                      <button onClick={() => handleDeleteEntry(entry)} className="text-red-600 hover:underline font-semibold">Delete</button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {balances.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">No payroll profiles yet.</td></tr>
                )}
              </tbody>
            </table>
          </ScrollX>
        )}
      </div>
    </div>
  );
};

export default PayrollLeaveTab;
